"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader, Send } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function MessageForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  return <section id="message-form" ref={ref} className={`py-16 lg:py-24 bg-dark-background text-foreground-dark transition-opacity duration-[var(--duration-medium)] ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4 max-w-2xl">
        <div className={`transform transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-display font-bold mb-4">
              Send Us a Message
            </h2>
            <p className="text-lg text-foreground-dark/80">
              We're here to help. Fill out the form below and we'll get back to you soon.
            </p>
          </div>

          <FormProvider className="bg-white/[0.08] backdrop-blur-md border border-white/10 rounded-[var(--radius-lg)] p-8 lg:p-10 shadow-strong" formId="message_form_contact" sectionName="message-form" defaultValues={{
          full_name: "",
          email: "",
          phone: "",
          subject: "",
          message: ""
        }} validationRules={{
          full_name: {
            required: true,
            minLength: 2,
            maxLength: 100
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: false,
            pattern: "phone"
          },
          subject: {
            required: false
          },
          message: {
            required: true,
            minLength: 10
          }
        }} submitText="Send Message" successMessage="Thank you for reaching out! We'll respond to your message shortly." errorMessage="We couldn't send your message. Please try again or call us directly.">
            <MessageFormContent />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function MessageFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="text-center py-8">
        <div className="inline-flex items-center justify-center size-16 rounded-full bg-primary/20 mb-6">
          <Send className="size-8 text-primary" />
        </div>
        <p className="text-xl font-semibold text-foreground-dark mb-2">
          Message Sent Successfully!
        </p>
        <p className="text-foreground-dark/80 mb-8">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:border-white/30">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <FormField control={control} name="full_name" rules={{
        required: "Your full name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        },
        maxLength: {
          value: 100,
          message: "Name cannot exceed 100 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground-dark">Your Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" className="bg-white/5 border-white/20 text-foreground-dark placeholder:text-foreground-dark/50 focus:border-primary focus:ring-primary/40" {...field} />
              </FormControl>
              <FormDescription className="text-foreground-dark/60 text-sm">
                First and last name
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground-dark">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" className="bg-white/5 border-white/20 text-foreground-dark placeholder:text-foreground-dark/50 focus:border-primary focus:ring-primary/40" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[\d\s()+-]+$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground-dark">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" className="bg-white/5 border-white/20 text-foreground-dark placeholder:text-foreground-dark/50 focus:border-primary focus:ring-primary/40" {...field} />
              </FormControl>
              <FormDescription className="text-foreground-dark/60 text-sm">
                Optional - we may call you back
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground-dark">Subject</FormLabel>
              <FormControl>
                <Input placeholder="Brief subject of your message" className="bg-white/5 border-white/20 text-foreground-dark placeholder:text-foreground-dark/50 focus:border-primary focus:ring-primary/40" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Message is required",
        minLength: {
          value: 10,
          message: "Message must be at least 10 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground-dark">Message</FormLabel>
              <FormControl>
                <Textarea placeholder="Please describe how we can help you..." className="bg-white/5 border-white/20 text-foreground-dark placeholder:text-foreground-dark/50 focus:border-primary focus:ring-primary/40 min-h-32" {...field} />
              </FormControl>
              <FormDescription className="text-foreground-dark/60 text-sm italic">
                Include any details you feel comfortable sharing. For clinical information, call us directly or use the secure portal.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-[var(--radius-md)] text-red-200">
            <p className="text-sm font-medium">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full h-12 bg-primary text-foreground-primary hover:bg-primary-hover active:bg-primary-active transition-all duration-[var(--duration-fast)] hover:scale-[1.02] active:scale-[0.98] shadow-medium">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Sending...
            </> : <>
              <Send className="mr-2 size-5" />
              Send Message
            </>}
        </Button>
      </div>
    </Form>;
}